/**
 * Phase 25 Plan 25-04 Task 2 — CommentComposer (GREEN).
 *
 * Markdown textarea + paperclip file picker + drag-drop zone + staged-attachment
 * chips + mention picker + WAIT-FOR-SERVER submit (D-16) with sequential
 * post-create attachment uploads (D-18) and per-file inline error reporting
 * (D-15 codes).
 *
 * Audience contract, flag `mks_532_comment_audience_checkbox` OFF (today):
 *   - `audience='internal'`: submit opens a "Mark as Internal / Mark as
 *     External" picker modal (UAT 2026-05-22 #5). The user must pick one —
 *     no default — and the choice drives `isInternalOnly`.
 *   - `audience='external'`: picker is NOT shown. Submit always sends
 *     `isInternalOnly: false` (defense-in-depth on top of the server-side
 *     enforcement; CMT-03).
 *
 * Audience contract, flag ON (MKS-532): the modal is gone and submit always
 * posts straight away.
 *   - `audience='internal'` on a project with NO live external collaborator:
 *     no audience control at all; every comment posts `isInternalOnly: true`.
 *   - `audience='internal'` with at least one: an "External" checkbox sits in
 *     the action row. Unchecked (the default) posts `isInternalOnly: true`,
 *     checked posts `false`. It resets after each successful post.
 *   - `audience='external'`: unchanged — no control, always `false`.
 */
import {
  useState,
  useRef,
  useCallback,
  useEffect,
  type FormEvent,
} from 'react';
import { useDropzone } from 'react-dropzone';
import { PaperClipIcon } from '@heroicons/react/24/outline';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { MentionPicker, type MentionPickerInsertion } from './MentionPicker';
import { TaskRefPicker, type TaskRefInsertion } from './TaskRefPicker';
import { AttachmentChip } from './AttachmentChip';
import { CannedReplyPicker, type CannedReplyContext } from './CannedReplyPicker';
import {
  useCreateComment,
  type AttachmentResult,
} from '../../hooks/useCreateComment';
import { useCommentAudience } from '../../hooks/useCommentAudience';
import { useFlag } from '../../services/flagsContext';
import { encodeMention } from './mentionEncoding';

// Zero-width spaces wrap each inserted mention name in the textarea so we can
// detect them at submit time without exposing the encoded link form to the
// user. Invisible to the human, robust to ordinary edits — backspacing through
// a mention removes both markers atomically since the user deletes the whole
// chunk; orphaned markers (rare) are stripped at submit.
const ZWSP = '\u200B';
const MENTION_MARKER_RE = new RegExp(`@${ZWSP}([^${ZWSP}]+?)${ZWSP}`, 'g');

interface TrackedMention {
  kind: 'user' | 'external_user';
  id: number;
}

const MAX_ATTACHMENTS = 10;

export interface CommentComposerProps {
  taskId: number;
  projectId: number;
  /** Drives Internal-only checkbox visibility AND mention-picker scope. */
  audience: 'internal' | 'external';
  /**
   * Optional placeholder text for the comment textarea.
   * Defaults to the standard internal prompt when omitted — existing callers
   * are unaffected.
   */
  placeholder?: string;
  /**
   * MKS-339 — values for the `{requester_name}` / `{ticket_code}` variables a
   * canned reply may use. Optional: a caller that does not pass it still gets
   * the picker, with both variables resolving to the empty string (never the
   * raw placeholder — see `renderCannedReply`).
   */
  cannedReplyContext?: CannedReplyContext;
  /**
   * Optional: fired after a successful submit so the parent can surface a
   * toast / scroll the comment list / etc. The `attachmentResults` array
   * lets the parent inspect per-file outcomes.
   */
  onPosted?: (result: { attachmentResults: AttachmentResult[] }) => void;
}

interface StagedAttachment {
  /** Stable id so React's reconciler can track chips even when the same name re-uploads. */
  uid: number;
  file: File;
  /** Set after the mutation completes if the upload failed for this file. */
  errorCode?: string;
  errorMessage?: string;
}

let nextStagedUid = 1;

export function CommentComposer({
  taskId,
  projectId,
  audience,
  placeholder,
  cannedReplyContext,
  onPosted,
}: CommentComposerProps) {
  const helpdeskOn = useFlag('mks_339_helpdesk_inbox');
  // MKS-532 — flag ON: no modal, internal by default, an "External" checkbox
  // only where there is actually a client who could read the comment.
  const autoInternalOn = useFlag('mks_532_comment_audience_checkbox');
  const [body, setBody] = useState('');
  // UAT 2026-05-22 #5 (flag OFF branch) — audience picker modal replaces the
  // legacy Internal-only checkbox + typed-confirm modal. Submit on internal
  // audience opens the picker; the chosen button drives `isInternalOnly`. No
  // default — the user MUST pick one explicitly.
  const [audiencePickerOpen, setAudiencePickerOpen] = useState(false);
  // MKS-532 (flag ON branch) — the single piece of audience state. False
  // means internal-only, which is the default for every new comment.
  const [postExternal, setPostExternal] = useState(false);
  const [staged, setStaged] = useState<StagedAttachment[]>([]);
  const [stageCapMessage, setStageCapMessage] = useState<string | null>(null);
  const [warningsNote, setWarningsNote] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  // MKS-532 — only the internal composer ever asks: the portal's reply
  // composer renders no audience control, and the endpoint is internal-only.
  const {
    data: commentAudience,
    isLoading: audienceLoading,
    isError: audienceError,
  } = useCommentAudience(projectId, autoInternalOn && audience === 'internal');
  // While the query is in flight or has failed we still RENDER the checkbox,
  // unchecked. Both of those states post internal by default, so neither can
  // leak; hiding the control on a failed fetch would instead make a
  // legitimate external comment silently impossible.
  const showExternalCheckbox =
    autoInternalOn &&
    audience === 'internal' &&
    (audienceLoading || audienceError || commentAudience?.hasExternalCollaborators === true);
  // name → kind/id map for inserted mentions; last-write-wins on duplicate names.
  const mentionsMapRef = useRef<Map<string, TrackedMention>>(new Map());
  const mutation = useCreateComment();

  const onDrop = useCallback((accepted: File[]) => {
    setStageCapMessage(null);
    setStaged((prev) => {
      const room = MAX_ATTACHMENTS - prev.length;
      if (room <= 0) {
        setStageCapMessage(
          `At most ${MAX_ATTACHMENTS} attachments per comment.`,
        );
        return prev;
      }
      if (accepted.length > room) {
        setStageCapMessage(
          `At most ${MAX_ATTACHMENTS} attachments per comment — only ${room} more accepted.`,
        );
      }
      const slice = accepted.slice(0, room).map((file) => ({
        uid: nextStagedUid++,
        file,
      }));
      return [...prev, ...slice];
    });
  }, []);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openPicker,
  } = useDropzone({
    onDrop,
    // 260809-ofp (D-02/D-03) — no `accept` filter: the backend now accepts
    // any content type on task/comment attachments, so the picker (and the
    // portal reply composer that renders through this component, finding
    // 10) must offer every file type.
    noClick: true,
    noKeyboard: true,
  });

  // Recompute the warnings note when the staged list changes (e.g. user
  // removes a failed chip → note should disappear).
  useEffect(() => {
    if (!warningsNote) return;
    const stillHasErrors = staged.some((s) => s.errorCode);
    if (!stillHasErrors) setWarningsNote(null);
  }, [staged, warningsNote]);

  function handleInsert(ins: MentionPickerInsertion) {
    const ta = taRef.current;
    if (!ta) return;
    // User-visible insertion: `@<ZWSP>Display Name<ZWSP> ` — invisible markers
    // bracket the name so transformBodyForSubmit can reliably re-encode at
    // POST time without leaking `mention:user:N` into the textarea.
    const visible = `@${ZWSP}${ins.displayName}${ZWSP} `;
    mentionsMapRef.current.set(ins.displayName, { kind: ins.kind, id: ins.id });
    const next =
      body.substring(0, ins.anchorIndex) + visible + body.substring(ins.partialEnd);
    setBody(next);
    // Restore caret AFTER the inserted visible token.
    const newCaret = ins.anchorIndex + visible.length;
    window.requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newCaret, newCaret);
    });
  }

  function handleTaskRefInsert(ins: TaskRefInsertion) {
    const ta = taRef.current;
    if (!ta) return;
    // 260819-kq3 — task refs are stored as-is (plain `#CODE` token, no hidden
    // encoding), so no submit-time transform and no tracking map are needed.
    const visible = `#${ins.code} `;
    const next =
      body.substring(0, ins.anchorIndex) + visible + body.substring(ins.partialEnd);
    setBody(next);
    const newCaret = ins.anchorIndex + visible.length;
    window.requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newCaret, newCaret);
    });
  }

  /**
   * MKS-339 — splice a rendered canned reply in at the caret.
   *
   * Unlike the two pickers above, there is no anchor token to replace: the
   * snippet is inserted at the current selection (replacing it when there is
   * one, which is what every editor does). The caret lands after the inserted
   * text so the agent keeps typing where the snippet ended.
   *
   * `selectionStart` is null only for input types that do not support it;
   * a textarea always has it, and the `?? body.length` fallback appends,
   * which is the sane answer if a browser ever disagrees.
   */
  function insertCannedReply(text: string) {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? body.length;
    const end = ta.selectionEnd ?? start;
    const next = body.substring(0, start) + text + body.substring(end);
    setBody(next);
    const newCaret = start + text.length;
    window.requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newCaret, newCaret);
    });
  }

  function transformBodyForSubmit(raw: string): string {
    // Replace `@<ZWSP>Name<ZWSP>` with the encoded markdown link form whenever
    // we have a tracked mention for `Name`. Orphaned markers (no matching
    // entry) collapse to plain `@Name`.
    return raw.replace(MENTION_MARKER_RE, (_, name: string) => {
      const entry = mentionsMapRef.current.get(name);
      if (!entry) return `@${name}`;
      return encodeMention(name, entry.kind, entry.id);
    });
  }

  function removeStaged(uid: number) {
    setStaged((prev) => prev.filter((s) => s.uid !== uid));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    // MKS-532 — flag ON: no forced choice. Internal is the default and the
    // checkbox (when it is shown at all) is the only way out of it.
    if (autoInternalOn) {
      runSubmit(!postExternal);
      return;
    }
    // Flag OFF: internal authors are forced through the audience picker
    // (UAT #5). External authors submit straight away — they can't write
    // internal comments anyway (server enforces; we mirror by sending false).
    if (audience === 'internal') {
      setAudiencePickerOpen(true);
      return;
    }
    runSubmit(false);
  }

  function runSubmit(isInternalOnly: boolean) {
    const trimmed = body.trim();
    if (!trimmed) return;
    const submitBody = transformBodyForSubmit(trimmed);
    // CMT-03 defense-in-depth: server forces false for externals; we mirror
    // it on the client too so a stale state value cannot leak through.
    const isInternalOnlyValue = audience === 'internal' ? isInternalOnly : false;
    setWarningsNote(null);
    setStageCapMessage(null);
    mutation.mutate(
      {
        taskId,
        body: submitBody,
        isInternalOnly: isInternalOnlyValue,
        files: staged.map((s) => s.file),
      },
      {
        onSuccess: ({ attachmentResults }) => {
          // MKS-532 — every comment starts internal again. Without this the
          // next reply in the thread would silently inherit "external".
          setPostExternal(false);
          // The parent comment was POSTed successfully. If any per-file
          // attachments failed (D-18), surface the error labels on those
          // chips and a top-level warnings note. The textarea is cleared
          // either way (the comment IS posted; users shouldn't have to
          // retype the body).
          const failuresByName = new Map<string, AttachmentResult>();
          for (const r of attachmentResults) {
            if (r.status === 'error') failuresByName.set(r.filename, r);
          }
          if (failuresByName.size === 0) {
            // Clean slate.
            setBody('');
            setStaged([]);
            mentionsMapRef.current.clear();
          } else {
            // Keep failing chips with their inline error label; drop the
            // successful ones.
            setBody('');
            mentionsMapRef.current.clear();
            setStaged((prev) =>
              prev
                .filter((s) => failuresByName.has(s.file.name))
                .map((s) => {
                  const err = failuresByName.get(s.file.name);
                  return err
                    ? {
                        ...s,
                        errorCode: err.code ?? 'UNKNOWN',
                        errorMessage: err.message,
                      }
                    : s;
                }),
            );
            setWarningsNote(
              'Comment posted, but some attachments failed to upload. Remove or replace the highlighted files and try again.',
            );
          }
          if (onPosted) onPosted({ attachmentResults });
        },
      },
    );
  }

  const canSubmit = body.trim().length > 0 && !mutation.isPending;
  const dropzoneClass = `relative rounded-lg border ${
    isDragActive
      ? 'border-dashed border-primary-400 bg-primary-50'
      : 'border-gray-200 bg-white'
  }`;

  return (
    <>
    <form onSubmit={handleSubmit} className="border-t border-gray-200 pt-3 space-y-2">
      <div
        {...getRootProps({
          className: dropzoneClass,
          'data-testid': 'composer-dropzone',
        })}
      >
        <input {...getInputProps({ 'aria-label': 'Attach files' })} />
        <textarea
          ref={taRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder={
            placeholder ??
            'Write a comment… (supports markdown, type @ to mention, # to reference a task)'
          }
          className="w-full text-sm text-gray-900 px-3 py-2 bg-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
          aria-label="Comment body"
        />
        <MentionPicker
          textareaRef={taRef}
          value={body}
          projectId={projectId}
          audience={audience}
          onInsert={handleInsert}
        />
        <TaskRefPicker
          textareaRef={taRef}
          value={body}
          projectId={projectId}
          audience={audience}
          excludeTaskId={taskId}
          onInsert={handleTaskRefInsert}
        />
      </div>

      {staged.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {staged.map((s) => (
            <AttachmentChip
              key={s.uid}
              file={s.file}
              errorCode={s.errorCode ?? null}
              errorMessage={s.errorMessage ?? null}
              onRemove={mutation.isPending ? undefined : () => removeStaged(s.uid)}
            />
          ))}
        </div>
      )}

      {stageCapMessage && (
        <p className="text-xs text-amber-700">{stageCapMessage}</p>
      )}

      {warningsNote && (
        <p className="text-xs text-amber-700">{warningsNote}</p>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={openPicker}
            disabled={mutation.isPending}
            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
            aria-label="Attach file"
          >
            <PaperClipIcon className="w-5 h-5" />
          </button>
          {/* MKS-339 — canned replies. Internal audience only: the snippets
              are agent-side boilerplate and the external portal composer has
              no business offering them. Renders nothing when the project has
              no replies or the helpdesk flag is off. */}
          {audience === 'internal' && (
            <CannedReplyPicker
              projectId={projectId}
              enabled={helpdeskOn}
              disabled={mutation.isPending}
              context={{
                requesterName: cannedReplyContext?.requesterName ?? null,
                ticketCode: cannedReplyContext?.ticketCode ?? null,
              }}
              onInsert={insertCannedReply}
            />
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* MKS-532 — the audience control, in the action row right next to
              Post comment so it cannot be missed. Emerald is the same cue the
              removed "Mark as External" modal button carried. */}
          {showExternalCheckbox && (
            <label
              className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-900 cursor-pointer hover:bg-emerald-100"
              data-testid="comment-post-external"
            >
              <input
                type="checkbox"
                checked={postExternal}
                onChange={(e) => setPostExternal(e.target.checked)}
                disabled={mutation.isPending}
                className="h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
              />
              External — visible to the client
            </label>
          )}
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
            isLoading={mutation.isPending}
          >
            Post comment
          </Button>
        </div>
      </div>

      {mutation.isError && (
        <p className="text-xs text-red-700">
          Couldn’t post comment: {mutation.error?.message ?? 'Server error'}
        </p>
      )}
    </form>
    {/* UAT 2026-05-22 #5 — audience picker, flag-OFF branch only (MKS-532
        removes it). Opens on Submit for internal authors. No default — the
        user must explicitly pick one of the two buttons. Cancel / Esc /
        overlay click aborts without posting so the body stays in the
        textarea for re-edit. */}
    {!autoInternalOn && (
    <Modal
      isOpen={audiencePickerOpen}
      onClose={() => setAudiencePickerOpen(false)}
      title="Who should see this comment?"
      size="md"
      footer={
        <div className="flex justify-end">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setAudiencePickerOpen(false)}
          >
            Cancel
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-gray-700">
          Pick the audience for this comment. The choice can not be changed
          after posting.
        </p>
        <button
          type="button"
          onClick={() => {
            setAudiencePickerOpen(false);
            runSubmit(true);
          }}
          disabled={mutation.isPending}
          className="w-full rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-left hover:border-rose-300 hover:bg-rose-100 focus:outline-none focus:ring-2 focus:ring-rose-400 disabled:opacity-50"
          data-testid="comment-audience-internal"
        >
          <div className="text-sm font-semibold text-rose-900">Mark as Internal</div>
          <div className="text-xs text-rose-800 mt-0.5">
            Hidden from external collaborators on this task. Visible to the
            internal team only.
          </div>
        </button>
        <button
          type="button"
          onClick={() => {
            setAudiencePickerOpen(false);
            runSubmit(false);
          }}
          disabled={mutation.isPending}
          className="w-full rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-left hover:border-emerald-300 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:opacity-50"
          data-testid="comment-audience-external"
        >
          <div className="text-sm font-semibold text-emerald-900">Mark as External</div>
          <div className="text-xs text-emerald-800 mt-0.5">
            Visible to everyone on this task — both internal team and
            external collaborators.
          </div>
        </button>
      </div>
    </Modal>
    )}
    </>
  );
}
